import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddSettings.css";
import SettingsTabs from "./SettingsTabs";
import { API_BASE } from "../../../ApiUrl";

const SETTINGS_API_URL = `${API_BASE}/tenant-settings/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const measurementSystems = [
  { value: "METRIC", label: "Metric" },
  { value: "IMPERIAL", label: "Imperial" },
  { value: "MIXED", label: "Mixed" }
];

const languageOptions = [
  { value: "en", label: "English" },
  { value: "te", label: "Telugu" },
  { value: "hi", label: "Hindi" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" }
];

const dateFormats = [
  { value: "DD-MM-YYYY", label: "DD-MM-YYYY" },
  { value: "MM-DD-YYYY", label: "MM-DD-YYYY" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY" }
];

const numberFormats = [
  { value: "1,234.56", label: "1,234.56" },
  { value: "1.234,56", label: "1.234,56" },
  { value: "1 234.56", label: "1 234.56" },
  { value: "1 234,56", label: "1 234,56" }
];

const emptySettings = {
  id: null,
  default_language: "en",
  date_format: "DD-MM-YYYY",
  number_format: "1,234.56",
  measurement_system: "METRIC",
  working_calendar: ""
};

const AddSettings = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ ...emptySettings });
  const [savedData, setSavedData] = useState({ ...emptySettings });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchExisting = async () => {
      const tenantUser = getTenantUser();

      if (!tenantUser?.tenant_id) {
        setFormData({ ...emptySettings });
        setSavedData({ ...emptySettings });
        setLoadingExisting(false);
        return;
      }

      try {
        setLoadingExisting(true);

        const response = await axios.post(
          TENANT_RECORDS_API_URL,
          {
            tenant_id: tenantUser.tenant_id,
            tables: ["tenant_settings"]
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const result = response.data;

        // Support several likely response shapes
        let rows = [];

        if (result?.data?.tenant_settings) {
          rows = result.data.tenant_settings;
        } else if (result?.tenant_settings) {
          rows = result.tenant_settings;
        } else if (Array.isArray(result?.data)) {
          rows = result.data;
        } else if (Array.isArray(result)) {
          rows = result;
        }

        const matched = Array.isArray(rows) && rows.length ? rows[0] : null;

        if (matched) {
          const loaded = {
            id: matched.id,
            default_language: matched.default_language || "en",
            date_format: matched.date_format || "DD-MM-YYYY",
            number_format: matched.number_format || "1,234.56",
            measurement_system: matched.measurement_system || "METRIC",
            working_calendar: matched.working_calendar || ""
          };
          setFormData(loaded);
          setSavedData(loaded);
        } else {
          setFormData({ ...emptySettings });
          setSavedData({ ...emptySettings });
        }
      } catch (error) {
        console.error("Error fetching tenant settings:", error);
        Swal.fire({
          icon: "error",
          title: "Unable to Load Settings",
          text: "Could not fetch tenant settings."
        });
        setFormData({ ...emptySettings });
        setSavedData({ ...emptySettings });
      } finally {
        setLoadingExisting(false);
      }
    };

    fetchExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  const buildPayload = (tenantUser) => ({
    default_language: formData.default_language,
    date_format: formData.date_format,
    number_format: formData.number_format,
    measurement_system: formData.measurement_system,
    working_calendar: (formData.working_calendar || "").trim(),
    tenant: tenantUser.tenant_id
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setFormData({ ...savedData });
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditing) return;

    const tenantUser = getTenantUser();

    if (!tenantUser) {
      Swal.fire({
        icon: "error",
        title: "Session Information Missing",
        text: "Tenant user information was not found in local storage."
      });
      return;
    }

    if (!tenantUser.tenant_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tenant User",
        text: "Tenant ID is missing from tenant_user."
      });
      return;
    }

    try {
      setLoading(true);

      const payload = buildPayload(tenantUser);

      if (formData.id) {
        await axios.put(
          `${SETTINGS_API_URL}${formData.id}/`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );
      } else {
        const response = await axios.post(SETTINGS_API_URL, payload, {
          headers: { "Content-Type": "application/json" }
        });

        const newId =
          response.data?.id ||
          response.data?.data?.id ||
          response.data?.pk;

        if (newId) {
          setFormData((prev) => ({ ...prev, id: newId }));
          setSavedData((prev) => ({ ...prev, id: newId }));
        }
      }

      const refreshed = { ...formData };
      setSavedData(refreshed);
      setIsEditing(false);

      await Swal.fire({
        icon: "success",
        title: "Saved Successfully",
        confirmButtonText: "OK"
      });
    } catch (error) {
      console.error("Save Tenant Settings Error:", error);
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: `Unable to save tenant settings. ${extractError(error)}`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="settings-content">
          <SettingsTabs />

          <div className="settings-page-header">
            <div>
              <h1>Tenant Settings</h1>
            </div>
          </div>

          {loadingExisting ? (
            <div className="settings-loading">
              Loading tenant settings...
            </div>
          ) : (
            <form className="settings-card" onSubmit={handleSubmit}>
              <div className="settings-card-header">
                <div>
                  <h2>Settings Configuration</h2>
                </div>
              </div>

              <div className="settings-card-body">
                <div className="settings-section">
                  <div className="settings-form-grid">
                    <div className="settings-field">
                      <label htmlFor="default_language">
                        Default Language <span>*</span>
                      </label>
                      <select
                        id="default_language"
                        name="default_language"
                        value={formData.default_language}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                      >
                        {languageOptions.map((language) => (
                          <option key={language.value} value={language.value}>
                            {language.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="settings-field">
                      <label htmlFor="date_format">
                        Date Format <span>*</span>
                      </label>
                      <select
                        id="date_format"
                        name="date_format"
                        value={formData.date_format}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                      >
                        {dateFormats.map((format) => (
                          <option key={format.value} value={format.value}>
                            {format.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="settings-field">
                      <label htmlFor="number_format">
                        Number Format <span>*</span>
                      </label>
                      <select
                        id="number_format"
                        name="number_format"
                        value={formData.number_format}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                      >
                        {numberFormats.map((format) => (
                          <option key={format.value} value={format.value}>
                            {format.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="settings-field">
                      <label htmlFor="measurement_system">
                        Measurement System <span>*</span>
                      </label>
                      <select
                        id="measurement_system"
                        name="measurement_system"
                        value={formData.measurement_system}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                      >
                        {measurementSystems.map((system) => (
                          <option key={system.value} value={system.value}>
                            {system.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="settings-section">
                  <div className="settings-section-title">
                    <h3>Working Calendar</h3>
                  </div>

                  <div className="settings-form-grid settings-calendar-grid">
                    <div className="settings-field">
                      <label htmlFor="working_calendar">
                        Working Calendar
                      </label>
                      <input
                        type="text"
                        id="working_calendar"
                        name="working_calendar"
                        value={formData.working_calendar}
                        onChange={handleChange}
                        disabled={!isEditing}
                        placeholder="e.g. India Working Calendar"
                        maxLength={100}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="settings-form-actions">
                {!isEditing ? (
                  <button
                    type="button"
                    className="settings-save-btn"
                    onClick={handleEdit}
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="settings-cancel-btn"
                      onClick={handleCancelEdit}
                      disabled={loading}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="settings-save-btn"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save Settings"}
                    </button>
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddSettings;